// rules.jsx
import React, { useEffect, useState, useRef } from 'react';
import './rules.css';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { initQuestions } from '../../functions/setUserQuestions';
import { selectAdminToken } from '../../admin/sessionSlice';
import { processQuestions } from '../../admin/questionSlice';
import { setBackButtonUrl } from '../uiSlice';
import IntroModal from '../introModal/introModal';


function UserRules() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { status: themeStatus, data: themeData } = useSelector(
    (state) => state.theme
  );
  const { status, user, error } = useSelector((state) => state.auth);
  const [redirect, setRedirect] = useState("/life");
  const [showIntroModal, setShowIntroModal] = useState(false);
  const [canProceedToNext, setCanProceedToNext] = useState(true);
  const adminToken = useSelector(selectAdminToken);
  const [isLoading, setIsLoading] = useState(false);
  const [misconfigured, setMisconfigured] = useState(false);
  // Guards the processQuestions -> setUserQuestions chain below so it can
  // only ever fire once per mount, no matter how many times this effect
  // re-runs (dependency changes, rapid re-render, fast double-click into
  // this page). setUserQuestions has no server-side protection against
  // being called twice for a brand-new user, so this is the one thing
  // standing between a double-fire here and a duplicate quiz attempt row.
  const initStartedRef = useRef(false);

  console.log(themeData);

  useEffect(() => {
    if (!themeData || !user || !adminToken) return;
    if (initStartedRef.current) return;
    initStartedRef.current = true;

    dispatch(processQuestions())
      .unwrap()                               // unwraps the thunk's payload or throws
      .then(() => {
        return initQuestions({
          userId: user.userId,
          email: user.email,
          fullName: user.name,
          themeName: themeData.themename,
          token: adminToken,
          isMultiplaySession: user.isMultiplaySession === true
        });
      })
      .then(data => {
        console.log('Init response:', data);
        // Only reveal the rules page (and its Next button) once the session is
        // initialized/reset. This guarantees an isMultiplaySession replay reset commits
        // before the quiz reads it, preventing a stale "already completed"
        // state from skipping straight to the thankyou page.
        setIsLoading(true);
      })
      .catch(err => {
        console.error('Error in process→init chain:', err);
        // noOfQuestion/flip isn't actually satisfiable by the questions the
        // admin has added -- don't let the quiz start in a broken state,
        // block it here with a message instead of silently running short.
        if (err.type === 'misconfigured') {
          setMisconfigured(true);
          // Same Warning/info popup style the admin side already uses for
          // this exact mismatch, so the experience is consistent across
          // both ends -- not just a plain static message here.
          Swal.fire('Warning', err.message, 'info');
        }
        // Don't trap the user on the loader if init fails.
        setIsLoading(true);
      });
  }, [themeData, user, adminToken]);

  useEffect(() => {
    if (!themeData) return;
    
    if (!themeData.lifelines) {
      // If lifelines is false, redirect to quiz
      setRedirect("/quiz");
      // Don't show modal on load, only when Next is clicked
    } else {
      // If lifelines is true, go to lifelines page
      setRedirect("/life");
    }
  }, [themeData]);

  useEffect(() => {
    dispatch(setBackButtonUrl("/login?&save=true"));
  }, [status, user]);

const handleIntroComplete = () => {
  setShowIntroModal(false);
  navigate(redirect); // Actually navigate to next page
};


  const handleCloseIntroModal = () => {
    setShowIntroModal(false);
    // If user closes without completing, still allow access
    // (you can change this behavior if needed)
    setCanProceedToNext(true);
  };

  const handleNextClick = (e) => {
    e.preventDefault();
    
    // If lifelines is false AND intro is enabled, show intro modal first
    if (!themeData.lifelines && themeData?.intro && themeData?.introFile) {
      setShowIntroModal(true);
      setCanProceedToNext(false);
      return;
    }
    
    // Otherwise proceed to next page directly
    navigate(redirect);
  };

  if (!isLoading) {
  return (
      <div className='quiz-loader-container'>
       <div className="quiz-loader"></div>
      </div>
  );
}

  if (misconfigured) {
    return (
      <div className='quiz-loader-container'>
        <p style={{ textAlign: 'center', padding: '0 20px' }}>
          This game isn't ready to play yet — it needs to be fixed in admin
          (number of questions to display doesn't match the questions
          available). Please check back later.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="user-rules-container">
        <div className="user-rules-left">
          {/* Replace with your image or content */}
          <img
            src={process.env.REACT_APP_S3_PATH + themeData?.logo}
            alt="Who wants to be a champion"
            className="user-rules-image"
          />
        </div>
        <div className="user-rules-right">
          <div className="user-rules-big-title">{themeData?.main_title}</div>
          <div className="user-rules-subline">
            {themeData?.landing_page_title}
          </div>
          <div className="user-rules-heading">Rules</div>
          <ul className="user-rules-list">
            {
              themeData?.rules.map((rules, index) => (
                <li key={index}>
                  <i
                    className="fa fa-arrow-right user-rule-icon"
                    aria-hidden="true"
                  />
                  {rules}
                </li>
              ))
            }
          </ul>
          <div style={{ textAlign: "center" }}>
            <button 
              className="user-next-button"
              onClick={handleNextClick}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Intro Modal */}
      <IntroModal
        isOpen={showIntroModal}
        onClose={handleCloseIntroModal}
        onComplete={handleIntroComplete}
        introFile={themeData?.introFile}
      />
    </>
  )
}

export default UserRules;